import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, MoreHorizontal } from "lucide-react";
import decarbonwizeLogo from "@/assets/decarbonwize-logo.png";
import carbonwizeFavicon from "@/assets/favicons/carbonwize.png";
import decarbonwizeFavicon from "@/assets/favicons/decarbonwize.png";
import sustainwizeFavicon from "@/assets/favicons/sustainwize.png";
import LanguageSelector from "./LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { getEnvironmentConfig } from "@/lib/environment";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DecarbonwizeNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();
  const { getLocalizedPath } = useLocalizedPath();

  // Get environment-specific configuration
  const { signInUrl } = getEnvironmentConfig();

  const productDropdown = [
    { name: "Carbonwize", href: "/", icon: carbonwizeFavicon },
    { name: "Decarbonwize", href: "/decarbonwize", icon: decarbonwizeFavicon },
    { name: "Sustainwize", href: "/sustainwize", icon: sustainwizeFavicon },
    { name: "Other services", href: "/other-services", icon: null },
  ];

  const resourcesDropdown = [
    { name: "Blogs", href: "/blog", isExternal: false },
    { name: t("nav.testimonials"), href: "/#testimonials" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[65px]">
          {/* Logo */}
          <Link to={getLocalizedPath("/decarbonwize")} className="flex items-center">
            <img src={decarbonwizeLogo} alt="Decarbonwize" style={{ width: '100px' }} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Product Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-base font-medium text-black hover:text-[#166a9c] transition-colors duration-200 outline-none">
                {t("nav.product")}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44 bg-background border border-border/50 shadow-lg rounded-lg p-2">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1.5">
                  Products
                </DropdownMenuLabel>
                {productDropdown.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link to={getLocalizedPath(item.href)} className="px-2 py-1.5 cursor-pointer text-sm text-foreground hover:bg-[#166a9c] hover:text-white rounded-md flex items-center gap-2">
                      {item.icon ? (
                        <img src={item.icon} alt="" className="w-5 h-5 rounded" />
                      ) : (
                        <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
                      )}
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* How It Works */}
            <a
              href="/#how-it-works"
              className="text-base font-medium text-black hover:text-[#166a9c] transition-colors duration-200"
            >
              {t("nav.howItWorks")}
            </a>

            {/* Pricing */}
            <a
              href="/#pricing"
              className="text-base font-medium text-black hover:text-[#166a9c] transition-colors duration-200"
            >
              {t("nav.pricing")}
            </a>

            {/* Resources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-base font-medium text-black hover:text-[#166a9c] transition-colors duration-200 outline-none">
                {t("nav.resources")}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44 bg-background border border-border/50 shadow-lg rounded-lg p-2">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1.5">
                  Resources
                </DropdownMenuLabel>
                {resourcesDropdown.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    {item.isExternal ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="px-2 py-1.5 cursor-pointer text-sm text-foreground hover:bg-[#166a9c] hover:text-white rounded-md">
                        {item.name}
                      </a>
                    ) : item.href.startsWith("#") ? (
                      <a href={item.href} className="px-2 py-1.5 cursor-pointer text-sm text-foreground hover:bg-[#166a9c] hover:text-white rounded-md">
                        {item.name}
                      </a>
                    ) : (
                      <Link to={getLocalizedPath(item.href)} className="px-2 py-1.5 cursor-pointer text-sm text-foreground hover:bg-[#166a9c] hover:text-white rounded-md">
                        {item.name}
                      </Link>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* About Us */}
            <Link
              to={getLocalizedPath("/about-us")}
              className="text-base font-medium text-black hover:text-[#166a9c] transition-colors duration-200"
              onClick={() => window.scrollTo(0, 0)}
            >
              {t("nav.aboutUs")}
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector hoverColor="#166a9c" />
            <Button variant="ghost" size="sm" className="hover:bg-transparent hover:text-[#166a9c]" asChild>
              <a href={signInUrl} target="_blank" rel="noopener noreferrer">
                {t("nav.signIn")}
              </a>
            </Button>
            <Button size="sm" className="bg-decarbonwize-primary hover:bg-[#166a9c] text-white" asChild>
              <Link to={getLocalizedPath("/contact-us")}>
                Book a Demo
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-border/50 animate-fade-in">
            <div className="flex flex-col gap-4">
              {/* About Us */}
              <Link
                to={getLocalizedPath("/about-us")}
                className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 py-2"
                onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
              >
                {t("nav.aboutUs")}
              </Link>

              {/* Product Section */}
              <div className="py-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("nav.product")}</p>
                <div className="pl-4 flex flex-col gap-1">
                  {productDropdown.map((item) => (
                    <Link
                      key={item.name}
                      to={getLocalizedPath(item.href)}
                      className="text-sm text-foreground hover:text-[#166a9c] py-1 flex items-center gap-2"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.icon ? (
                        <img src={item.icon} alt="" className="w-5 h-5 rounded" />
                      ) : (
                        <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
                      )}
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* How It Works */}
              <a
                href="/#how-it-works"
                className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 py-2"
                onClick={() => setIsOpen(false)}
              >
                {t("nav.howItWorks")}
              </a>

              {/* Pricing */}
              <a
                href="/#pricing"
                className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 py-2"
                onClick={() => setIsOpen(false)}
              >
                {t("nav.pricing")}
              </a>

              {/* Resources Section */}
              <div className="py-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t("nav.resources")}</p>
                <div className="pl-4 flex flex-col gap-1">
                  {resourcesDropdown.map((item) => (
                    item.isExternal ? (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-foreground hover:text-[#166a9c] py-1"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </a>
                    ) : item.href.startsWith("#") ? (
                      <a
                        key={item.name}
                        href={item.href}
                        className="text-sm text-foreground hover:text-[#166a9c] py-1"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={getLocalizedPath(item.href)}
                        className="text-sm text-foreground hover:text-[#166a9c] py-1"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-4 border-t border-border/50">
                <LanguageSelector />
                <Button variant="outline" className="w-full" asChild>
                  <a href={signInUrl} target="_blank" rel="noopener noreferrer">
                    {t("nav.signIn")}
                  </a>
                </Button>
                <Button className="w-full bg-decarbonwize-primary hover:bg-[#166a9c] text-white" asChild>
                  <Link to={getLocalizedPath("/contact-us")} onClick={() => setIsOpen(false)}>
                    Book a Demo
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default DecarbonwizeNavbar;
